/**
 * UserAvatar - Fetches avatar via backend proxy to avoid Google CORS/referrer issues
 */

import { useState, useEffect } from 'react';
import { useAuth, clearSessionIf401 } from '../../hooks/useAuth';

const RAW = (import.meta.env.VITE_API_URL || 'http://localhost:3002').replace(/\/$/, '');
const API_BASE = RAW.endsWith('/api') ? RAW : `${RAW}/api`;

interface UserAvatarProps {
  user: { name: string; email: string; picture?: string } | null;
  size?: 'sm' | 'md';
  className?: string;
}

export function UserAvatar({ user, size = 'md', className = '' }: UserAvatarProps) {
  const { token } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [directFailed, setDirectFailed] = useState(false);

  const sizeClass = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';

  useEffect(() => {
    if (!user?.picture || !token) {
      setAvatarUrl(null);
      return;
    }
    setDirectFailed(false);
    let objectUrl: string | null = null;
    const url = `${API_BASE}/auth/avatar`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (clearSessionIf401(r)) return Promise.reject(new Error('Session expired'));
        return r.ok ? r.blob() : Promise.reject(new Error('Avatar fetch failed'));
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setAvatarUrl(objectUrl);
      })
      .catch(() => setAvatarUrl(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [user?.picture, token]);

  if (!user) return null;

  // Proxy avatar loaded
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={user.name}
        className={`${sizeClass} rounded-full border-2 border-brand-blue object-cover ${className}`}
      />
    );
  }

  // Try direct URL only for non-Google URLs (Google blocks direct embedding)
  const isGooglePicture = user.picture?.includes('googleusercontent.com');
  if (user.picture && !directFailed && !isGooglePicture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        className={`${sizeClass} rounded-full border-2 border-brand-blue object-cover ${className}`}
        referrerPolicy="no-referrer"
        onError={() => setDirectFailed(true)}
      />
    );
  }

  // Initials fallback (always used when proxy fails for Google pics)
  const initial = user.name?.charAt(0)?.toUpperCase() || '?';
  return (
    <div
      className={`${sizeClass} rounded-full bg-gray-600 flex items-center justify-center text-white text-sm font-medium ${className}`}
      title={user.name}
    >
      {initial}
    </div>
  );
}
